import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import DataTable, { createTheme } from 'react-data-table-component';

import api from '../../services/api';
import EditUnitModal from './EditUnitModal';
import ViewUnitModal from './ViewUnitModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import UploadInventoryModal from './UploadInventoryModal';

// 1) Theme definition (unchanged)
createTheme('unitsTheme', {
    text: {
        primary: '#212529',
        secondary: '#495057',
    },
    background: {
        default: '#ffffff',
    },
    context: {
        background: '#e3f2fd',
        text: '#212529',
    },
    divider: {
        default: '#dee2e6',
    },
    action: {
        button: '#fff',
        hover: 'rgba(0,0,0,0.08)',
        disabled: 'rgba(0,0,0,0.12)',
    },
});

// 2) Custom styles (unchanged)
const customStyles = {
    rows: {
        style: {
            minHeight: '50px',
            fontSize: '15px',
        },
    },
    headCells: {
        style: {
            backgroundColor: '#f8f9fa',
            fontWeight: '600',
            fontSize: '1rem',
        },
    },
    cells: {
        style: {
            fontSize: '0.95rem',
        },
    },
};

// Unit types (unchanged)
const UNIT_TYPES = [
    'duplex', 'apartment', 'villa', 'studio', 'townhouse', 'twinhouse', 'standalone villa', 'chalet', 'cabin', 'office', 'clinic', 'retail', 'serviced studio', 'serviced apartment', 'branded villa', 'branded apartment', 'beach house,iVilla',
];

// Update UnitsTable to accept projectData as prop
const UnitsTable = ({ projectData, projectId: propProjectId }) => {
    // Use projectId from props if available, otherwise from URL params
    const { projectId: urlProjectId } = useParams();
    const projectId = propProjectId || urlProjectId;

    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [currentUnit, setCurrentUnit] = useState(null);
    const [editedUnit, setEditedUnit] = useState(null);

    // Modals states
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [deletedUnit, setDeletedUnit] = useState(null);
    const [showUploadInventoryModal, setShowUploadInventoryModal] = useState(false);
    const [showBrokerFileInfoModal, setShowBrokerFileInfoModal] = useState(false);
    
    // Use project data from props or initialize empty
    const [inventory_file_updated_at, setInventory_file_updated_at] = useState(null);
    const [projectName, setProjectName] = useState('');

    // Filters (unchanged)
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    // --- MODIFIED: Fetch units with pagination and filters ---
    const fetchUnits = useCallback(async (page, limit) => {
        setLoading(true);
        try {
            const params = {
                page: page, // Pass current page
                per_page: limit, // Pass items per page
            };
            if (statusFilter) {
                params.status = statusFilter;
            }
            if (typeFilter) {
                params.type = typeFilter;
            }

            const res = await api.get(`/units/byProject/${projectId}`, { params });

            if (res.data?.response?.data) {
                setUnits(res.data.response.data);
                setTotalRows(res.data.response.total || 0);
            } else {
                setUnits([]);
                setTotalRows(0);
            }
        } catch (error) {
            console.error('Error fetching units:', error);
            toast.error('Failed to fetch units.');
            setUnits([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [projectId, statusFilter, typeFilter]); // Dependencies for useCallback

    // --- Initial fetch and fetch on filter/pagination change ---
    // Use a ref to prevent duplicate API calls on initial render
    const initialRenderRef = React.useRef(true);
    
    useEffect(() => {
        // This will prevent the double API call on initial render
        // but still allow calls when filters/pagination change
        if (initialRenderRef.current) {
            initialRenderRef.current = false;
            fetchUnits(currentPage, perPage);
            return;
        }
        
        fetchUnits(currentPage, perPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, typeFilter, currentPage, perPage, projectId]);
    
    // Add back the function to fetch inventory file update time
    const fetchInventoryFileUpdateTime = useCallback(async () => {
        try {
            const response = await api.get(`/projects/${projectId}`);
            console.log('Project API response:', response.data); // Debug log
            // Use the correct path to the inventory_file_updated_at property
            const updatedAt = response.data?.response?.project?.inventory_file_updated_at;
            setInventory_file_updated_at(updatedAt);
            
            // Also store the project name
            const name = response.data?.response?.project?.name;
            setProjectName(name || '');
        } catch (error) {
            console.error('Error fetching inventory file update time:', error);
        }
    }, [projectId]);

    // Use a ref to prevent double API call for project data
    const initialProjectDataFetchRef = React.useRef(true);

    // Separate useEffect for project data
    useEffect(() => {
        if (initialProjectDataFetchRef.current) {
            initialProjectDataFetchRef.current = false;
            fetchInventoryFileUpdateTime();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // --- Pagination Handlers ---
    const handlePageChange = (page) => {
        setCurrentPage(page); // Update current page state
        // fetchUnits will be called by the useEffect hook
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage); // Update perPage state
        setCurrentPage(page);    // Reset to page 1 or use the provided page
        // fetchUnits will be called by the useEffect hook
    };

    // View Modal Logic (unchanged, but added fetchUnits on close for consistency)
    const handleView = (unit) => {
        setCurrentUnit(unit);
        setShowViewModal(true);
    };
    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setCurrentUnit(null);
        //fetchUnits(currentPage, perPage); // Re-fetch current page data
    };

    // Edit Modal Logic (unchanged, but added fetchUnits on save/close)
    const handleEdit = (unit) => {
        setEditedUnit(unit);
        setShowEditModal(true);
    };
    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditedUnit(null);
        //fetchUnits(currentPage, perPage); // Re-fetch current page data
    };
    const handleSaveEdit = (updatedUnit) => {
        setShowEditModal(false);
        setEditedUnit(null);
        fetchUnits(currentPage, perPage); // Re-fetch current page data after saving
        // Optionally show a success toast
        toast.success('Unit updated successfully!');
    };

    // Delete Modal Logic (unchanged, but added fetchUnits on successful delete/close)
    const handleDeleteClick = (unit) => {
        setDeletedUnit(unit);
        setShowDeleteConfirmationModal(true);
    };
    const handleCloseDeleteModal = () => {
        setShowDeleteConfirmationModal(false);
        setDeletedUnit(null);
        // No fetch here, only fetch after successful deletion or if cancelling
        // If just closing without deleting, maybe re-fetch if needed: fetchUnits(currentPage, perPage);
    };
    const handleDelete = async () => {
        if (!deletedUnit) return;
        try {
            await api.delete(`/units/${deletedUnit.id}`);
            toast.success('Deletion requested successfully! Wait for approval.');
            handleCloseDeleteModal(); // Close modal first
            fetchUnits(currentPage, perPage); // Re-fetch data after successful delete
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error requesting deletion.');
            console.error(error);
            handleCloseDeleteModal(); // Still close modal on error
        }
    };

    // CSV Export Headers (unchanged)
    const csvHeaders = [
        { label: 'Type', key: 'type' },
        { label: 'Area', key: 'area' },
        { label: '# Bedrooms', key: 'numberOfBedrooms' },
        { label: 'Created At', key: 'created_at' },
        { label: 'Status', key: 'status' },
    ];

    // Columns Definition (unchanged)
    const columns = [
        {
            name: 'Type',
            selector: (row) => row.type || '',
            sortable: true, // Note: Sorting might require server-side implementation too
        },
        {
            name: 'Area',
            selector: (row) => row.area || '',
            sortable: true,
        },
        {
            name: '# Bedrooms',
            selector: (row) => row.numberOfBedrooms || '',
            sortable: true,
        },
        {
            name: 'Created At',
            selector: (row) => row.created_at || '',
            sortable: true,
            cell: (row) =>
                row.created_at
                    ? new Date(row.created_at).toLocaleString()
                    : 'N/A',
        },
        {
            name: 'Status',
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => {
                const value = row.status;
                let color = 'warning';
                let label = 'Pending';
                if (value === 'approved') {
                    color = 'success';
                    label = 'Approved';
                } else if (value === 'rejected') {
                    color = 'danger';
                    label = 'Rejected';
                }
                return (
                    <span
                        className={`bg-${color}-focus text-${color}-main px-24 py-4 rounded-pill fw-medium text-sm`}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {label}
                    </span>
                );
            },
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="d-flex">
                    {/* View */}
                    <button
                        onClick={() => handleView(row)}
                        className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0" // Added border-0
                    >
                        <Icon icon="iconamoon:eye-light" />
                    </button>
                    {/* Edit */}
                    <button
                        onClick={() => handleEdit(row)}
                        className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0" // Added border-0
                    >
                        <Icon icon="lucide:edit" />
                    </button>
                    {/* Delete */}
                    <button
                        onClick={() => handleDeleteClick(row)}
                        className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0" // Added border-0
                    >
                        <Icon icon="mingcute:delete-2-line" />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '150px',
        },
    ];

    // Filtered data for CSV (Now uses 'units' directly as filtering is handled by API)
    // If you want CSV export to *only* reflect the current page, keep using 'units'.
    // If you want CSV export for *all* filtered items across pages, you'd need a separate API call
    // or modify fetchUnits to fetch all data when exporting. For simplicity, this exports current page.
    const filteredExportData = units;

    // Add new handler for broker file info
    const handleBrokerFileClick = () => {
        setShowBrokerFileInfoModal(true);
    };

    const handleProceedToUpload = () => {
        setShowBrokerFileInfoModal(false);
        setShowUploadInventoryModal(true);
    };

    return (
        <div className="card basic-data-table px-3 py-3">
            {/* Only show project name in h4 if it's not already in the breadcrumb */}
            {!projectData && <h4 className="mb-3">{projectName ? `${projectName} - Units` : 'Units'}</h4>}
            
            {/* Filters & Export (Layout unchanged) */}
            <div className="d-flex justify-content-between mb-3 align-items-center flex-wrap gap-2">
                {/* Status Filter */}
                <div className="d-flex align-items-center gap-2">
                    <label className="fw-medium mb-0">Status:</label>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: '150px' }}
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">All</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="pending">Pending</option>
                    </select>
                </div>

                {/* Type Filter */}
                <div className="d-flex align-items-center gap-2">
                    <label className="fw-medium mb-0">Type:</label>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: '180px' }}
                        value={typeFilter}
                        onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">All Types</option>
                        {UNIT_TYPES.map((t) => (
                            <option key={t} value={t}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Action Buttons */}
                <div className="d-flex gap-2">
                    {/* Upload Inventory Button */}
                    <button
                        className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                        onClick={handleBrokerFileClick}
                    >
                        <Icon icon="mdi:upload" />
                         Upload Daily Inventory For Broker to Download
                        <small className="ms-2 text-light">
                            {inventory_file_updated_at
                                ? `(Last updated: ${new Date(inventory_file_updated_at).toLocaleString()})`
                                : '(No update time)'}
                        </small>
                    </button>

                    {/* CSV Export */}
                    <CSVLink
                        data={filteredExportData}
                        headers={csvHeaders}
                        filename={`units-project-${projectId}-page-${currentPage}.csv`}
                        className="btn btn-secondary btn-sm"
                    >
                        Export Added Units As CSV
                    </CSVLink>
                </div>
            </div>

            {/* --- MODIFIED: DataTable with Server-Side Pagination Props --- */}
            <DataTable
                columns={columns}
                data={units}
                progressPending={loading} // Show loading indicator
                pagination // Enable pagination
                paginationServer // Indicate server-side pagination
                paginationTotalRows={totalRows} // Total rows from API
                paginationDefaultPage={currentPage} // Control current page
                onChangeRowsPerPage={handlePerRowsChange} // Handle rows per page change
                onChangePage={handlePageChange} // Handle page change
                paginationRowsPerPageOptions={[10, 15, 20, 50]} // Options for rows per page
                theme="unitsTheme"
                customStyles={customStyles}
                highlightOnHover
                responsive
                striped
                noDataComponent={loading ? "" : "No results found"} // Show "No results" only when not loading
            />

            {/* View Modal (Unchanged) */}
            <ViewUnitModal
                show={showViewModal}
                handleClose={handleCloseViewModal}
                unit={currentUnit}
            />

            {/* Edit Modal (Unchanged) */}
            <EditUnitModal
                show={showEditModal}
                handleClose={handleCloseEditModal}
                unit={editedUnit}
                handleSave={handleSaveEdit} // Pass the save handler
            />

            {/* Delete Confirmation Modal (Unchanged) */}
            <DeleteConfirmationModal
                show={showDeleteConfirmationModal}
                handleClose={handleCloseDeleteModal}
                handleDelete={handleDelete}
                content={
                    <>
                        <Modal.Header closeButton> {/* Added closeButton */}
                            <Modal.Title>Delete Unit</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Are you sure you want to request deletion for this Unit?</p>
                            {deletedUnit && (
                                <small>Type: {deletedUnit.type}, Area: {deletedUnit.area}</small>
                            )}
                        </Modal.Body>
                        {/* Footer buttons handled by DeleteConfirmationModal component presumably */}
                    </>
                }
            />

            {/* Broker File Info Modal */}
            <Modal
                show={showBrokerFileInfoModal}
                onHide={() => setShowBrokerFileInfoModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Broker Download File Information</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>This upload is specifically for providing brokers with a downloadable Excel file containing the project's inventory.</p>
                    <p className="mb-0"><strong>Important Notes:</strong></p>
                    <ul>
                        <li>This file is for broker downloads only</li>
                        <li>This does NOT replace adding units through the "Add New Unit" button</li>
                        <li>All unit modules (1br,2br etc..) with starting prices must still be added individually using the "Add New Unit" button in Units Tab</li>
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowBrokerFileInfoModal(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleProceedToUpload}
                    >
                        Proceed to Upload
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Upload Inventory Modal */}
            <UploadInventoryModal
                show={showUploadInventoryModal}
                handleClose={() => setShowUploadInventoryModal(false)}
                onSuccess={() => {
                    fetchUnits(currentPage, perPage);
                    fetchInventoryFileUpdateTime(); // Call this to refresh update time
                }}
                inventory_file_updated_at={inventory_file_updated_at}
            />
        </div>
    );
};

export default UnitsTable;